import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Plus, Star, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PerformanceManagement() {
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('goals');
  const [goalOpen, setGoalOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [editGoalOpen, setEditGoalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ user_id: '', title: '', description: '', target_date: '' });
  const [reviewForm, setReviewForm] = useState({ user_id: '', review_period: '', overall_rating: 3, strengths: '', areas_of_improvement: '', comments: '' });
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const canManage = user && (user.role === 'admin' || user.role === 'team_leader');

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${API}/hrms/performance/goals`, { headers: getHeaders() });
      setGoals(res.data);
    } catch (e) { toast.error('Failed to fetch goals'); }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/hrms/performance/reviews`, { headers: getHeaders() });
      setReviews(res.data);
    } catch (e) { toast.error('Failed to fetch reviews'); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getHeaders() });
      setAllUsers(res.data);
    } catch (err) { console.error("./pages/PerformanceManagement.jsx:", err); }
  };

  useEffect(() => { fetchGoals(); fetchReviews(); if (canManage) fetchUsers(); }, [user]);

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hrms/performance/goals`, goalForm, { headers: getHeaders() });
      toast.success('Goal created'); setGoalOpen(false);
      setGoalForm({ user_id: '', title: '', description: '', target_date: '' });
      fetchGoals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/hrms/performance/goals/${selectedGoal.id}`, editForm, { headers: getHeaders() });
      toast.success('Goal updated'); setEditGoalOpen(false); fetchGoals();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/hrms/performance/goals/${goalId}`, { headers: getHeaders() });
      toast.success('Goal deleted'); fetchGoals();
    } catch (e) { toast.error('Failed to delete goal'); }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hrms/performance/reviews`, reviewForm, { headers: getHeaders() });
      toast.success('Review created'); setReviewOpen(false);
      setReviewForm({ user_id: '', review_period: '', overall_rating: 3, strengths: '', areas_of_improvement: '', comments: '' });
      fetchReviews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const RatingStars = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => <Star key={`star-${i}`} size={14} className={i <= rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'} />)}
    </div>
  );

  const progressColor = (p) => p >= 80 ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="performance-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Performance</h1>
          <p className="text-slate-600">Goals tracking and performance reviews</p>
        </div>
        <div className="flex items-center gap-3">
          {canManage && tab === 'goals' && (
            <Button data-testid="add-goal-btn" onClick={() => setGoalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Add Goal</Button>
          )}
          {canManage && tab === 'reviews' && (
            <Button data-testid="add-review-btn" onClick={() => setReviewOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Add Review</Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded p-1 mb-6 w-fit">
        <button data-testid="tab-goals" onClick={() => setTab('goals')} className={`px-4 py-2 rounded text-sm font-bold transition-colors ${tab === 'goals' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-blue-900'}`}>Goals ({goals.length})</button>
        <button data-testid="tab-reviews" onClick={() => setTab('reviews')} className={`px-4 py-2 rounded text-sm font-bold transition-colors ${tab === 'reviews' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-blue-900'}`}>Reviews ({reviews.length})</button>
      </div>

      {tab === 'goals' && (
        <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900">Employee</TableHead>
                <TableHead className="font-bold text-blue-900">Goal</TableHead>
                <TableHead className="font-bold text-blue-900">Target Date</TableHead>
                <TableHead className="font-bold text-blue-900">Progress</TableHead>
                <TableHead className="font-bold text-blue-900">Status</TableHead>
                <TableHead className="font-bold text-blue-900">Rating</TableHead>
                {canManage && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {goals.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 7 : 6} className="text-center text-slate-500 py-8"><Target size={48} className="mx-auto mb-2 text-slate-300" />No goals set</TableCell></TableRow>
              ) : goals.map(goal => (
                <TableRow key={goal.id} data-testid={`goal-row-${goal.id}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900">{goal.user_name}</TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{goal.title}</p>
                    {goal.description && <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[250px]">{goal.description}</p>}
                  </TableCell>
                  <TableCell className="text-sm">{goal.target_date || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${progressColor(goal.progress)}`} style={{ width: `${goal.progress}%` }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700">{goal.progress}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${goal.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : goal.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'}`}>{goal.status?.replace('_', ' ')}</span>
                  </TableCell>
                  <TableCell>{goal.rating ? <RatingStars rating={goal.rating} /> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`edit-goal-${goal.id}`} onClick={() => { setSelectedGoal(goal); setEditForm({ progress: goal.progress, status: goal.status, rating: goal.rating || '', review_notes: goal.review_notes || '' }); setEditGoalOpen(true); }} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50"><Edit2 size={14} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-goal-${goal.id}`} onClick={() => handleDeleteGoal(goal.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-4">
          {reviews.length === 0 ? (
            <div className="bg-white border border-blue-100 rounded shadow-sm p-12 text-center">
              <Star size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No performance reviews yet</p>
            </div>
          ) : reviews.map(review => (
            <div key={review.id} data-testid={`review-${review.id}`} className="bg-white border border-blue-100 rounded shadow-sm p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-bold text-blue-900 text-lg">{review.user_name}</p>
                  <p className="text-xs text-slate-500">{review.review_period} — Reviewed by {review.reviewer_name}</p>
                </div>
                <RatingStars rating={review.overall_rating} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded p-3">
                  <p className="text-xs font-bold uppercase text-emerald-800 mb-1">Strengths</p>
                  <p className="text-sm text-slate-700">{review.strengths || '-'}</p>
                </div>
                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs font-bold uppercase text-amber-800 mb-1">Areas of Improvement</p>
                  <p className="text-sm text-slate-700">{review.areas_of_improvement || '-'}</p>
                </div>
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-xs font-bold uppercase text-blue-800 mb-1">Comments</p>
                  <p className="text-sm text-slate-700">{review.comments || '-'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Goal Dialog */}
      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Create Goal</DialogTitle>
            <DialogDescription>Set a performance goal for an employee</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGoal} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Employee</Label>
              <select data-testid="goal-user-select" value={goalForm.user_id} onChange={(e) => setGoalForm({ ...goalForm, user_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                <option value="">Select employee</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              <div className="mt-2">
                <SearchableSelect value={goalForm.user_id} onChange={(id) => setGoalForm({ ...goalForm, user_id: id })}
                  items={allUsers} getSublabel={(u) => u.role}
                  emptyLabel="Select employee" placeholder="Search employee…" testId="goal-user" />
              </div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Goal Title</Label>
              <Input data-testid="goal-title" value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <textarea data-testid="goal-desc" value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Goal details..." /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Target Date</Label>
              <Input data-testid="goal-target-date" type="date" value={goalForm.target_date} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
            <Button type="submit" data-testid="submit-goal-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Create Goal</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={editGoalOpen} onOpenChange={setEditGoalOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Update Goal — {selectedGoal?.title}</DialogTitle>
            <DialogDescription>Update progress, status, and rating</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateGoal} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Progress (%)</Label>
              <Input data-testid="goal-progress" type="number" min="0" max="100" value={editForm.progress || 0} onChange={(e) => setEditForm({ ...editForm, progress: parseInt(e.target.value) || 0 })} className="mt-2 rounded border-blue-200" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
              <select data-testid="goal-status" value={editForm.status || ''} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                <option value="in_progress">In Progress</option><option value="completed">Completed</option><option value="on_hold">On Hold</option>
              </select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Rating (1-5)</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <button key={`edit-star-${i}`} type="button" onClick={() => setEditForm({ ...editForm, rating: i })} className="p-1">
                    <Star size={24} className={i <= (editForm.rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-slate-200'} />
                  </button>
                ))}
              </div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Review Notes</Label>
              <textarea data-testid="goal-review-notes" value={editForm.review_notes || ''} onChange={(e) => setEditForm({ ...editForm, review_notes: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Notes..." /></div>
            <Button type="submit" data-testid="submit-edit-goal-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Update Goal</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Create Performance Review</DialogTitle>
            <DialogDescription>Write a performance review for an employee</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateReview} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Employee</Label>
              <select data-testid="review-user-select" value={reviewForm.user_id} onChange={(e) => setReviewForm({ ...reviewForm, user_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                <option value="">Select employee</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              <div className="mt-2">
                <SearchableSelect value={reviewForm.user_id} onChange={(id) => setReviewForm({ ...reviewForm, user_id: id })}
                  items={allUsers} getSublabel={(u) => u.role}
                  emptyLabel="Select employee" placeholder="Search employee…" testId="review-user" />
              </div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Review Period</Label>
              <Input data-testid="review-period" value={reviewForm.review_period} onChange={(e) => setReviewForm({ ...reviewForm, review_period: e.target.value })} required className="mt-2 rounded border-blue-200" placeholder="e.g., Q1 2026" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Overall Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <button key={`review-star-${i}`} type="button" onClick={() => setReviewForm({ ...reviewForm, overall_rating: i })} className="p-1">
                    <Star size={24} className={i <= reviewForm.overall_rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'} />
                  </button>
                ))}
              </div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Strengths</Label>
              <textarea data-testid="review-strengths" value={reviewForm.strengths} onChange={(e) => setReviewForm({ ...reviewForm, strengths: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Areas of Improvement</Label>
              <textarea data-testid="review-improvement" value={reviewForm.areas_of_improvement} onChange={(e) => setReviewForm({ ...reviewForm, areas_of_improvement: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Comments</Label>
              <textarea data-testid="review-comments" value={reviewForm.comments} onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" /></div>
            <Button type="submit" data-testid="submit-review-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Submit Review</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
